import { IconBrandNationalRegistry } from '@components/icons/brand-national-registry';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';

import { useStore } from 'models/Provider';

const nationalRegistryFieldToExternalQuery = (filter: FilterData) => {
  if (!filter.field) return {};
  const field = filter.field as string;

  if (field === 'gender') {
    let value = Array.isArray(filter.value) ? [...filter.value] : [filter.value];

    // Each gender has two corresponding values (underage and adult).
    // We only display one for simplicity, but we need to filter for both.
    // 1 and 3 are male, 2 and 4 are female, 7 and 8 are non-binary/other.
    value.includes('1') && value.push('3');
    value.includes('2') && value.push('4');
    value.includes('7') && value.push('8');

    switch (filter.comparer) {
      case 'is':
      case 'any_of':
        return { [`ssn_registry__${field}__any_of`]: value };
      case 'not_is':
      case 'none_of':
        return { [`ssn_registry__${field}__none_of`]: value };
      default:
        return {};
    }
  } else {
    const value = String(filter.value);

    switch (filter.comparer) {
      case 'is':
        return { [`ssn_registry__${field}`]: value.toLowerCase() };
      case 'starts':
        return { [`ssn_registry__${field}__startswith`]: value.toLowerCase() };
      case 'not_is':
        return { [`ssn_registry__${field}__is_not`]: value.toLowerCase() };
      case 'contains':
        return { [`ssn_registry__${field}__contains`]: value.toLowerCase() };
      case 'not_contains':
        return { [`ssn_registry__${field}__not_contains`]: value.toLowerCase() };
      case 'gt':
        return { [`ssn_registry__${field}__gt`]: value };
      case 'lt':
        return { [`ssn_registry__${field}__lt`]: value };
      default:
        return {};
    }
  }
};

export const useNationalRegistryFilter = (): FilterConfigField | null => {
  const { t, current } = useStore();

  if (!current.info.useSsnRegistry) return null;

  return {
    id: 'nationalRegistry',
    label: t('applicants.filters.national_registry.title'),
    icon: <IconBrandNationalRegistry size={24} />,
    type: 'category',
    fields: [
      {
        id: 'name',
        label: t('applicants.filters.national_registry.name'),
        type: 'text',
        allowedComparers: ['is', 'not_is', 'starts'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'gender',
        label: t('applicants.filters.national_registry.gender'),
        type: 'single_select',
        values: [
          { value: '1', label: t('ssnregistry.gender.1') },
          { value: '2', label: t('ssnregistry.gender.2') },
          { value: '7', label: t('ssnregistry.gender.7') },
        ],
        allowedComparers: ['is', 'not_is', 'any_of', 'none_of'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'ssn',
        label: t('applicants.filters.national_registry.ssn'),
        type: 'text',
        allowedComparers: ['is', 'not_is', 'contains', 'not_contains'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'age',
        label: t('applicants.filters.national_registry.age'),
        type: 'number',
        allowedComparers: ['is', 'not_is', 'gt', 'lt'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'age_year_end',
        label: t('applicants.filters.national_registry.age_year_end'),
        type: 'number',
        allowedComparers: ['is', 'not_is', 'gt', 'lt'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'street',
        label: t('applicants.filters.national_registry.street'),
        type: 'text',
        allowedComparers: ['is', 'not_is', 'contains', 'not_contains'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'city',
        label: t('applicants.filters.national_registry.city'),
        type: 'text',
        allowedComparers: ['is', 'not_is', 'contains', 'not_contains'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
      {
        id: 'postal_code',
        label: t('applicants.filters.national_registry.postal_code'),
        type: 'text',
        allowedComparers: ['is', 'not_is'],
        toExternalQuery: nationalRegistryFieldToExternalQuery,
      },
    ],
  };
};
